import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Table, Button, Modal, Space, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, PictureOutlined } from '@ant-design/icons';
import BASEURL from '../API/Config';

const Offers = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editOfferId, setEditOfferId] = useState(null);
  const [existingImages, setExistingImages] = useState([]);
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const token = localStorage.getItem('token');

  const fetchOffers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASEURL}/master/getoffer`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setOffers(data);
    } catch {
      Swal.fire('Error', 'Failed to load offers.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOffers(); }, []);

  const openAddModal = () => {
    setIsEditing(false);
    setEditOfferId(null);
    setExistingImages([]);
    setRemovedImageIds([]);
    setSelectedFile(null);
    setPreview(null);
    setShowModal(true);
  };

  const openEditModal = (offer) => {
    setIsEditing(true);
    setEditOfferId(offer._id);
    setExistingImages(offer.images || []);
    setRemovedImageIds([]);
    setSelectedFile(null);
    setPreview(null);
    setShowModal(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
    if (file) setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile && existingImages.length === 0) {
      Swal.fire('Error', 'Please select an image.', 'error');
      return;
    }
    setIsSubmitting(true);
    const formData = new FormData();
    removedImageIds.forEach((id) => formData.append('removeImages', id));
    if (selectedFile) formData.append('images', selectedFile);
    try {
      const url = isEditing
        ? `${BASEURL}/master/updateoffer/${editOfferId}`
        : `${BASEURL}/master/createoffer`;
      const res = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error(await res.text());
      Swal.fire('Success', isEditing ? 'Offer updated!' : 'Offer added!', 'success');
      setShowModal(false);
      fetchOffers();
    } catch (err) {
      Swal.fire('Error', err.message || 'Failed.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Delete Offer?', text: 'This cannot be undone.', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#e53935', confirmButtonText: 'Yes, Delete',
    }).then(async (result) => {
      if (result.isConfirmed) {
        await fetch(`${BASEURL}/master/deleteoffer/${id}`, {
          method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
        });
        Swal.fire('Deleted!', 'Offer removed.', 'success');
        fetchOffers();
      }
    });
  };

  const columns = [
    {
      title: '#', key: 'idx', width: 55,
      render: (_, __, i) => <span style={{ color: '#6b7280' }}>{i + 1}</span>,
    },
    {
      title: 'OFFER IMAGES', key: 'images',
      render: (r) => (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {r.images?.map((img) => (
            <img key={img._id} src={`${BASEURL}${img.url}`} alt="offer"
              style={{ width: 120, height: 75, objectFit: 'cover', borderRadius: 6, border: '1px solid #e0e0e0' }} />
          ))}
        </div>
      ),
    },
    {
      title: 'STATUS', key: 'status',
      render: () => <Tag color="green" style={{ borderRadius: 50, fontWeight: 600 }}>Active</Tag>,
    },
    {
      title: 'ACTIONS', key: 'actions', width: 120,
      render: (r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(r)}
            style={{ color: '#2874f0', borderColor: '#2874f0', borderRadius: 4 }} />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(r._id)}
            style={{ borderRadius: 4 }} />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>Masters</div>
          <h2 style={{ margin: 0, fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 700, color: '#212121' }}>Offers Master</h2>
        </div>
        {offers.length === 0 && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}
            style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, height: 38, fontWeight: 600 }}>
            Add Offer
          </Button>
        )}
      </div>

      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e8ecf0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <Table columns={columns} dataSource={offers} rowKey="_id" loading={loading}
          pagination={{ pageSize: 10 }} size="middle" />
      </div>

      <Modal
        title={<span style={{ fontFamily: 'Outfit,sans-serif', fontWeight: 700 }}>{isEditing ? 'Edit Offer' : 'Add Offer'}</span>}
        open={showModal} onCancel={() => setShowModal(false)} footer={null} centered width={500}
      >
        <form onSubmit={handleSubmit} style={{ marginTop: 16 }}>
          {isEditing && existingImages.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Current Image</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {existingImages.map((img) => (
                  <div key={img._id} style={{ position: 'relative' }}>
                    <img src={`${BASEURL}${img.url}`} alt="" style={{ width: 120, height: 75, objectFit: 'cover', borderRadius: 6, border: '1px solid #e0e0e0' }} />
                    <button type="button" onClick={() => {
                      setRemovedImageIds(p => [...p, img._id]);
                      setExistingImages(p => p.filter(i => i._id !== img._id));
                    }} style={{ position: 'absolute', top: -6, right: -6, background: '#e53935', border: 'none', borderRadius: '50%', width: 20, height: 20, color: '#fff', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
            <PictureOutlined style={{ marginRight: 6, color: '#2874f0' }} />Upload Offer Image
          </div>
          <input type="file" accept="image/*" onChange={handleFileChange}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, marginBottom: 12 }} />

          {preview && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Preview:</div>
              <img src={preview} alt="preview" style={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 8, border: '1px solid #e0e0e0' }} />
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={isSubmitting}
              style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, fontWeight: 600 }}>
              {isEditing ? 'Update Offer' : 'Add Offer'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Offers;
